import type { Feature } from 'ol';
import type Geometry from 'ol/geom/Geometry';
import type {
  AttributeValue,
  GeometryKind,
  LayerModel,
  LogicalFinca,
  LogicalSuerte,
  LogicalTablon,
  OriginalAttributes,
  PhysicalFragment,
  VectorSourceFormat,
} from '../core/types';

export interface ParsedPhysicalFeature {
  readonly feature: Feature<Geometry>;
  readonly attributes: OriginalAttributes;
  readonly sourceIndex: number;
}

const text = (value: AttributeValue | undefined): string =>
  value === null || value === undefined ? '' : String(value).trim();

const normalizedField = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase();
const field = (attributes: OriginalAttributes, ...aliases: string[]): AttributeValue | undefined => {
  const wanted = new Set(aliases.map(normalizedField));
  const match = Object.keys(attributes).find((name) => wanted.has(normalizedField(name)));
  return match ? attributes[match] : undefined;
};

const number = (value: AttributeValue | undefined): number => {
  const parsed = Number.parseFloat(text(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

interface FincaNameCandidate { readonly value: string; readonly authority: number; readonly source: string }

const fincaNameCandidate = (attributes: OriginalAttributes, fincaCode: string): FincaNameCandidate | null => {
  if (fincaCode === '__sin_finca__') return { value: 'Sin finca', authority: 0, source: 'fallback' };
  const authoritativeFields = ['Hacienda', 'NombreFinca', 'NomFinca', 'Finca'] as const;
  for (const name of authoritativeFields) {
    const explicit = text(field(attributes, name));
    if (explicit && normalizedField(explicit) !== normalizedField(fincaCode)) return { value: explicit, authority: 0, source: name };
  }
  const nombre = text(field(attributes, 'Nombre'));
  const suerte = text(field(attributes, 'Suerte'));
  if (nombre && suerte) {
    const suffix = new RegExp(`\\s+${suerte.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}$`, 'i');
    const inferred = nombre.replace(suffix, '').trim();
    if (inferred && inferred !== nombre) return { value: inferred, authority: 1, source: 'Nombre − Suerte' };
  }
  // Nombre is accepted only as a lower-authority observation. Agreement and
  // conflicts are audited across every record sharing the same Codfinca.
  if (nombre && normalizedField(nombre) !== normalizedField(fincaCode)) return { value: nombre, authority: 2, source: 'Nombre' };
  return null;
};

export function resolveFincaDisplayName(fincaCode: string, candidates: readonly FincaNameCandidate[]): { name: string; conflict?: string } {
  if (fincaCode === '__sin_finca__') return { name: 'Sin finca' };
  if (!candidates.length) return { name: `Finca ${fincaCode}` };
  const authority = Math.min(...candidates.map((candidate) => candidate.authority));
  const eligible = candidates.filter((candidate) => candidate.authority === authority);
  const counts = new Map<string, { value: string; count: number; sources: Set<string> }>();
  eligible.forEach((candidate) => {
    const key = normalizedField(candidate.value);
    const current = counts.get(key) ?? { value: candidate.value, count: 0, sources: new Set<string>() };
    current.count += 1; current.sources.add(candidate.source); counts.set(key, current);
  });
  const ranked = [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'es', { sensitivity: 'base' }));
  const name = ranked[0]?.value ?? `Finca ${fincaCode}`;
  if (ranked.length < 2) return { name };
  return { name, conflict: `Codfinca ${fincaCode}: nombres contradictorios (${ranked.map(({ value }) => `“${value}”`).join(', ')}); se usó “${name}” por frecuencia y orden estable.` };
}

const geometryKind = (features: readonly ParsedPhysicalFeature[]): GeometryKind => {
  const kinds = new Set(features.map(({ feature }) => feature.getGeometry()?.getType() ?? 'Unknown'));
  if ([...kinds].every((kind) => kind.includes('Polygon'))) return 'Polygon';
  if ([...kinds].every((kind) => kind.includes('LineString'))) return 'LineString';
  if ([...kinds].every((kind) => kind.includes('Point'))) return 'Point';
  return kinds.size > 1 ? 'Mixed' : 'Unknown';
};

export function buildLayerModel(
  name: string,
  sourceFormat: Exclude<VectorSourceFormat, 'WORK'>,
  parsed: readonly ParsedPhysicalFeature[],
  sourceCrs = 'EPSG:4326',
): LayerModel {
  const warnings: string[] = [];
  const fragmentList: PhysicalFragment[] = [];
  const fragmentsById = new Map<string, PhysicalFragment>();
  const fincaDrafts = new Map<string, { code: string; names: FincaNameCandidate[]; suertes: Set<string>; fragments: string[]; area: number }>();
  const suerteDrafts = new Map<string, { code: string; label: string; fincaId: string; tablones: Set<string>; fragments: string[]; area: number }>();
  const tablonDrafts = new Map<string, { code: string; suerteId: string; fragments: string[]; area: number }>();

  parsed.forEach(({ feature, attributes, sourceIndex }) => {
    const fincaCode = text(field(attributes, 'Codfinca', 'CodFinca', 'CodigoFinca')) || '__sin_finca__';
    const suerteCode = text(field(attributes, 'CodSuerte', 'CodigoSuerte')) || text(field(attributes, 'Suerte')) || `__sin_suerte_${sourceIndex}`;
    const suerteLabel = text(field(attributes, 'Suerte')) || suerteCode;
    const tablonCode = text(field(attributes, 'Tablon', 'Tablón', 'CodTablon')) || '__sin_tablon__';
    const fincaId = `finca:${fincaCode}`;
    const suerteId = `${fincaId}/suerte:${suerteCode}`;
    const tablonId = `${suerteId}/tablon:${tablonCode}`;
    const sourceFid = text(field(attributes, 'FID', 'id')) || String(sourceIndex);
    const fragmentId = `${tablonId}/fragment:${sourceFid}:${sourceIndex}`;
    const area = number(field(attributes, 'Area_Ha', 'AreaHa'));
    const fragment: PhysicalFragment = { id: fragmentId, feature, attributes, fincaId, suerteId, tablonId };
    feature.set('fragmentId', fragmentId, true);
    fragmentList.push(fragment);
    fragmentsById.set(fragmentId, fragment);

    const finca = fincaDrafts.get(fincaId) ?? { code: fincaCode, names: [], suertes: new Set(), fragments: [], area: 0 };
    const nameCandidate = fincaNameCandidate(attributes, fincaCode);
    if (nameCandidate) finca.names.push(nameCandidate);
    finca.suertes.add(suerteId); finca.fragments.push(fragmentId); finca.area += area; fincaDrafts.set(fincaId, finca);
    const suerte = suerteDrafts.get(suerteId) ?? { code: suerteCode, label: suerteLabel, fincaId, tablones: new Set(), fragments: [], area: 0 };
    suerte.tablones.add(tablonId); suerte.fragments.push(fragmentId); suerte.area += area; suerteDrafts.set(suerteId, suerte);
    const tablon = tablonDrafts.get(tablonId) ?? { code: tablonCode, suerteId, fragments: [], area: 0 };
    tablon.fragments.push(fragmentId); tablon.area += area; tablonDrafts.set(tablonId, tablon);
  });

  const fincas = new Map<string, LogicalFinca>();
  fincaDrafts.forEach((value, id) => {
    const resolved = resolveFincaDisplayName(value.code, value.names);
    if (resolved.conflict) warnings.push(resolved.conflict);
    fincas.set(id, { id, code: value.code, name: resolved.name, suerteIds: [...value.suertes], fragmentIds: value.fragments, areaHa: value.area, declaredAreaHa: value.area });
  });
  const suertes = new Map<string, LogicalSuerte>();
  suerteDrafts.forEach((value, id) => suertes.set(id, { id, code: value.code, label: value.label, fincaId: value.fincaId, tablonIds: [...value.tablones], fragmentIds: value.fragments, areaHa: value.area, declaredAreaHa: value.area }));
  const tablones = new Map<string, LogicalTablon>();
  tablonDrafts.forEach((value, id) => tablones.set(id, { id, code: value.code, suerteId: value.suerteId, fragmentIds: value.fragments, areaHa: value.area, declaredAreaHa: value.area }));
  const fields = [...new Set(parsed.flatMap(({ attributes }) => Object.keys(attributes)))];
  const missingFinca = fragmentList.filter((fragment) => fragment.fincaId.includes('__sin_finca__')).length;
  if (missingFinca) warnings.push(`${missingFinca} geometría(s) sin Codfinca se conservaron bajo “Sin finca”.`);
  if (!fragmentList.length) warnings.push('La fuente no contiene geometrías compatibles.');

  return {
    id: `layer:${Date.now()}`,
    name,
    sourceFormat,
    sourceCrs,
    readOnly: true,
    geometryKind: geometryKind(parsed),
    attributeFields: fields,
    fragments: fragmentList,
    fragmentsById,
    fincas,
    suertes,
    tablones,
    warnings,
  };
}
